import type { AxiosRequestConfig } from 'axios'
import type { ZodType } from 'zod'

import { ApiError } from './api-error'
import { apiClient } from './client'

/**
 * Typed entry point for all HTTP calls. Pass a Zod schema to get both
 * compile-time types and runtime validation of the response - a backend
 * contract change surfaces as a VALIDATION_ERROR instead of undefined
 * behavior somewhere in the UI.
 *
 *   const users = await request({ url: '/users' }, z.array(userSchema))
 */
export async function request<T>(config: AxiosRequestConfig, schema?: ZodType<T>): Promise<T> {
  const response = await apiClient.request<unknown>(config)

  if (!schema) {
    return response.data as T
  }

  const result = schema.safeParse(response.data)
  if (!result.success) {
    throw new ApiError({
      code: 'VALIDATION_ERROR',
      message: `Response from ${config.url ?? 'unknown endpoint'} failed schema validation`,
      details: result.error.issues,
    })
  }

  return result.data
}
